import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import type { ChatEvent, PlatformAccounts } from '../../api'
import type { Destination, Platform } from '../types'
import { MARK, MarkImg, MONO, PLATFORM_LABEL } from '../ui'
import { t, type Key } from '../../i18n'
import { ChatsSoon, Soon, WatchStrip } from './Right'
import { ACCOUNT_LABEL, type AccountPlatform } from './useLiveScreen'
import type { ChatItem, ChatStore } from './useChat'
import { Icon } from '../../ui'

/*
 * Chats mode of the Studio's right column (docs/live-studio.md §3.6, docs/chat.md): the strip of
 * outputs, filters by output and by kind, one feed of every chat the center reads, and the
 * outputs whose chat is not read with what to do about it. Every string of a chat is plain text:
 * React renders it as text nodes; links are not clickable; an emote is an <img> only when its
 * fragment carries an image on a platform CDN.
 */

export type ChatFilter = 'all' | string
type View = 'all' | 'qa' | 'highlights'

const HIGHLIGHT_KINDS = ['paid', 'membership', 'raid', 'announcement']

/** Hosts emote images may come from (docs/chat.md: `img` only from the platform CDN). */
const EMOTE_HOSTS = ['jtvnw.net', 'kick.com']

export function safeEmoteSrc(src: string | undefined): string | undefined {
  if (!src) return undefined
  try {
    const u = new URL(src)
    if (u.protocol !== 'https:') return undefined
    return EMOTE_HOSTS.some((h) => u.hostname === h || u.hostname.endsWith(`.${h}`)) ? u.href : undefined
  } catch {
    return undefined
  }
}

/** The author's colour made readable on the theme's background (lightness clamped), or undefined. */
export function readableColor(c: string | undefined, theme: string | undefined): string | undefined {
  const m = c ? /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(c.trim()) : null
  if (!m) return undefined
  const hex = m[1]!.length === 3 ? m[1]!.replace(/./g, (x) => x + x) : m[1]!
  const r = parseInt(hex.slice(0, 2), 16) / 255
  const g = parseInt(hex.slice(2, 4), 16) / 255
  const b = parseInt(hex.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  let l = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4
    h *= 60
  }
  // dark background: at least 62 % light; light background: at most 36 %
  l = theme === 'light' ? Math.min(l, 0.36) : Math.max(l, 0.62)
  return `hsl(${Math.round(h)} ${Math.round(Math.min(s, 0.9) * 100)}% ${Math.round(l * 100)}%)`
}

const theme = () => (typeof document !== 'undefined' ? document.documentElement.dataset.theme : undefined)

function platformOfChat(p: string): Platform {
  return p === 'twitch' || p === 'kick' || p === 'youtube' || p === 'facebook' || p === 'instagram' ? p : 'rtmp'
}

/** Small platform tile of a message (.sx-msg .sx-tile): the owner's mark when we have it, else the monogram. */
function Mark({ platform, title, size = 18 }: { platform: Platform; title?: string; size?: number }) {
  const mark = MARK[platform]
  return (
    <span title={title ?? PLATFORM_LABEL[platform]} style={{ width: size, height: size }}
      className={`sx-tile text-[8px] leading-none ${mark ? 'mark' : ''}`}>
      {mark ? <MarkImg mark={mark} /> : MONO[platform]}
    </span>
  )
}

const ROLE: Record<string, Key> = { owner: 'chat.role.owner', mod: 'chat.role.mod', vip: 'chat.role.vip', staff: 'chat.role.staff' }

const LOOK: Record<string, { box: string; label: string; key: Key }> = {
  paid: { box: 'border border-warn/50 bg-warn/[.06]', label: 'text-warn', key: 'chat.kind.paid' },
  membership: { box: 'border border-start/45 bg-start/[.05]', label: 'text-start', key: 'chat.kind.membership' },
  raid: { box: 'border border-live/45 bg-live/[.05]', label: 'text-live', key: 'chat.kind.raid' },
  announcement: { box: 'border border-accent/45 bg-accent/[.05]', label: 'text-accent', key: 'chat.kind.announcement' },
}

/** The label of a highlighted item: its kind and the amount (display, or value + unit, tier, months, count, viewers). */
export function amountLabel(e: ChatEvent): string {
  const look = LOOK[e.kind]
  const parts: string[] = look ? [t(look.key)] : []
  const a = e.amount
  if (a) {
    if (a.display) parts.push(a.display)
    else if (a.value !== undefined) parts.push(a.unit ? `${a.value} ${a.unit}` : String(a.value))
    if (!a.display) {
      if (a.tier) parts.push(t('chat.tier', { n: a.tier }))
      if (a.months) parts.push(t('chat.months', { n: a.months }))
      if (a.count) parts.push(t('chat.gifts', { n: a.count }))
      if (a.viewers !== undefined) parts.push(t('chat.viewers', { n: a.viewers }))
    }
  }
  return parts.join(' · ')
}

/** The text of a message from its fragments, as text nodes. */
function Body({ e }: { e: ChatEvent }) {
  const frs = e.fragments?.length ? e.fragments : [{ t: 'text' as const, text: e.text }]
  return (
    <>
      {frs.map((f, i) => {
        const text = typeof f.text === 'string' ? f.text : ''
        if (f.t === 'emote') {
          const src = safeEmoteSrc(f.img)
          if (src) {
            return <img key={i} data-testid="chat-emote" src={src} alt={text} title={text} referrerPolicy="no-referrer" loading="lazy" draggable={false}
              className="inline-block h-[1.4em] w-auto max-w-[5em] align-middle" />
          }
          return <Fragment key={i}>{text}</Fragment>
        }
        if (f.t === 'mention') return <span key={i} data-testid="chat-mention" className="rounded-sm bg-accent/[.10] px-0.5 font-medium text-accent">{text}</span>
        if (f.t === 'link') return <span key={i} data-testid="chat-link" className="break-all text-text-soft underline decoration-dotted underline-offset-2">{text}</span>
        return <Fragment key={i}>{text}</Fragment>
      })}
    </>
  )
}

function timeOf(at: string): string {
  const d = new Date(at)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

function Message({ e, dest, showOutput, phone }: { e: ChatEvent; dest?: Destination; showOutput: boolean; phone?: boolean }) {
  const platform = platformOfChat(e.platform)
  if (e.kind === 'system') {
    return (
      <li data-testid="chat-msg" data-id={e.id} data-kind="system" data-output={e.output_id} className="flex gap-2 px-2 py-1 text-[12px] italic text-muted">
        <Mark platform={platform} title={dest?.name} />
        <span className="min-w-0 flex-1 break-words"><Body e={e} /></span>
      </li>
    )
  }
  const look = LOOK[e.kind]
  const color = readableColor(e.author?.color, theme())
  const roles = (e.author?.roles ?? []).filter((r) => r in ROLE)
  const label = look ? amountLabel(e) : ''
  return (
    <li data-testid="chat-msg" data-id={e.id} data-kind={e.kind} data-output={e.output_id}
      className={`flex flex-col gap-0.5 rounded-md px-2 py-1.5 ${look ? look.box : e.flags?.highlighted ? 'bg-accent/[.06]' : ''}`}>
      {look && <span data-testid="chat-amount" className={`font-mono text-[10px] font-semibold tracking-[.08em] ${look.label}`}>{label}</span>}
      {e.reply_to?.author_name && <span className="truncate pl-[26px] text-[11px] text-muted">↳ {t('chat.replyTo', { name: e.reply_to.author_name })}</span>}
      <div className="flex gap-2">
        <Mark platform={platform} title={dest?.name} />
        <div className="min-w-0 flex-1 break-words text-[13px] leading-snug">
          {e.author && (
            <span data-testid="chat-author" className="font-semibold text-text" style={color ? { color } : undefined}>{e.author.name}</span>
          )}
          {roles.map((r) => (
            <span key={r} data-testid={`chat-role-${r}`} className={`ml-1 rounded-sm border px-1 font-mono text-[9px] font-semibold tracking-[.06em] ${r === 'mod' ? 'border-live/50 text-live' : r === 'owner' ? 'border-accent/50 text-accent' : 'border-line-2 text-text-soft'}`}>{t(ROLE[r]!)}</span>
          ))}
          {e.flags?.first && <span data-testid="chat-first" className="ml-1 font-mono text-[9px] font-semibold tracking-[.06em] text-accent">{t('chat.first')}</span>}
          {showOutput && dest && <span className="ml-1 text-[10px] text-muted">· {dest.name}</span>}
          <span className="text-text-soft">{e.author ? ' ' : ''}<Body e={e} /></span>
        </div>
        {!phone && <span className="num flex-none pt-0.5 font-mono text-[10px] text-faint">{timeOf(e.at)}</span>}
      </div>
    </li>
  )
}

function Chip({ on, onClick, children, title, testId }: { on: boolean; onClick: () => void; children: ReactNode; title?: string; testId?: string }) {
  return (
    <button type="button" role="radio" aria-checked={on} onClick={onClick} title={title} data-testid={testId}
      className={`flex min-h-8 flex-none items-center gap-1.5 rounded-md border px-2 text-xs max-md:min-h-[40px] ${on ? 'border-accent bg-accent/[.08] text-text' : 'border-line-2 text-text-soft hover:text-text'}`}>
      {children}
    </button>
  )
}

/** The feed: follows new items at the bottom; scrolled up it pauses with "Paused · N new ↓". */
function Feed({ items, dests, phone, resetKey }: { items: ChatItem[]; dests: Destination[]; phone?: boolean; resetKey: string }) {
  const ref = useRef<HTMLOListElement>(null)
  const [paused, setPaused] = useState(false)
  const [pausedAt, setPausedAt] = useState(0)
  const lastN = items.length ? items[items.length - 1]!.n : 0
  const lastRef = useRef(lastN)
  lastRef.current = lastN

  const toBottom = () => {
    const el = ref.current
    if (el) el.scrollTop = el.scrollHeight
  }
  useLayoutEffect(() => {
    if (!paused) toBottom()
  }, [lastN, items.length, paused])
  // another filter: back to live
  useEffect(() => {
    setPaused(false)
    toBottom()
  }, [resetKey])

  const onScroll = () => {
    const el = ref.current
    if (!el) return
    const away = el.scrollHeight - el.scrollTop - el.clientHeight > 32
    if (away && !paused) {
      setPaused(true)
      setPausedAt(lastRef.current)
    } else if (!away && paused) setPaused(false)
  }
  const fresh = paused ? items.filter((i) => i.n > pausedAt).length : 0
  const byOut = new Map(dests.map((d) => [d.outputId, d]))
  const perPlatform = new Map<string, number>()
  for (const d of dests) if (d.chat?.access === 'reads') perPlatform.set(d.platform, (perPlatform.get(d.platform) ?? 0) + 1)

  return (
    <div className="relative flex min-h-0 flex-1 flex-col max-md:min-h-[260px]">
      <ol ref={ref} onScroll={onScroll} data-testid="chat-feed" data-paused={paused ? 'true' : 'false'} aria-label={t('chat.feed')} aria-live="off"
        className="m-0 flex min-h-0 flex-1 list-none flex-col gap-0.5 overflow-y-auto p-2 max-md:max-h-[60dvh]">
        {items.map((i) => {
          const d = byOut.get(i.e.output_id) ?? dests.find((x) => x.id === i.e.destination_id)
          return <Message key={i.e.id} e={i.e} dest={d} phone={phone} showOutput={(perPlatform.get(platformOfChat(i.e.platform)) ?? 0) > 1} />
        })}
      </ol>
      {paused && (
        <button type="button" data-testid="chat-paused" onClick={() => { setPaused(false); toBottom() }}
          className="absolute bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-accent bg-panel px-3 py-1 text-[11px] text-accent shadow-pop max-md:min-h-[40px]">
          {fresh > 0 ? t('chat.paused', { n: fresh }) : t('chat.pausedNone')}
        </button>
      )}
    </div>
  )
}

/** What an output whose chat is not read needs, as a line and maybe a button. */
function AccessLine({ d, connecting, onConnect }: { d: Destination; connecting: AccountPlatform | null; onConnect: (p: AccountPlatform) => void }) {
  const c = d.chat
  const platform = PLATFORM_LABEL[d.platform]
  const p = d.platform in ACCOUNT_LABEL ? (d.platform as AccountPlatform) : undefined
  let text: string
  let action: 'connect' | 'reconnect' | undefined
  let tone = 'text-muted'
  if (!c || c.access === 'unsupported') {
    text = c?.reason === 'coming_later' ? t('chat.access.comingLater', { platform }) : c?.reason === 'not_configured' ? t('chat.access.notConfigured', { platform })
      : d.platform === 'rtmp' ? t('chat.access.custom') : t('chat.access.unsupported', { platform })
  } else if (c.access === 'connect') {
    text = c.reason === 'other_channel' ? t('chat.access.otherChannel', { platform }) : t('chat.access.connect', { platform })
    action = 'connect'
    tone = c.reason === 'other_channel' ? 'text-warn' : 'text-text-soft'
  } else if (c.access === 'reconnect') {
    text = t('chat.access.reconnect', { platform })
    action = 'reconnect'
    tone = 'text-warn'
  } else if (c.state === 'reconnecting' && (c.reason === 'authorization' || c.reason?.startsWith('revoked_'))) {
    // The platform refused or withdrew our access: only connecting again helps.
    text = t('chat.access.reconnect', { platform })
    action = 'reconnect'
    tone = 'text-warn'
  } else if (c.state === 'reconnecting') {
    text = t('chat.access.dropped')
    tone = 'text-warn'
  } else {
    text = ''
  }
  return (
    <li data-testid={`chat-access-${d.id}`} data-access={c?.access ?? 'unsupported'} data-reason={c?.reason ?? ''} className="flex items-center gap-2.5 py-1.5">
      <Mark platform={d.platform} size={22} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px]">{d.name}</span>
        {text && <span className={`block text-[11px] ${tone}`}>{text}</span>}
      </span>
      {c?.access === 'reads' && c.state !== 'reconnecting' && <span className="flex-none font-mono text-[10px] font-semibold tracking-[.08em] text-live"><Icon name="check" size={12} className="mr-1 inline align-[-2px]" />{t('chats.reads')}</span>}
      {action && p && (
        <button type="button" data-testid={`chat-connect-${d.id}`} disabled={!!connecting} onClick={() => onConnect(p)}
          className="min-h-8 flex-none text-xs text-accent underline-offset-2 hover:underline disabled:text-faint max-md:min-h-[44px]">
          {connecting === p ? t('chats.opening', { platform: ACCOUNT_LABEL[p] }) : <span className="inline-flex items-center gap-1">{action === 'reconnect' ? t('chat.reconnect') : t('chats.connect')}<Icon name="external" size={12} /></span>}
        </button>
      )}
    </li>
  )
}

/**
 * Chats mode. A center without chats (no output has `chat`) keeps the earlier screen: who will
 * be able to read chat, from the connected accounts.
 */
export function Chats({ dests, accounts, now, connecting, onConnect, onOutputs, onFix, chat, filter, onFilter, phone }: {
  dests: Destination[]; accounts?: PlatformAccounts; now: number; connecting: AccountPlatform | null;
  onConnect: (p: AccountPlatform) => void; onOutputs: () => void; onFix: (d: Destination) => void;
  chat: ChatStore; filter: ChatFilter; onFilter: (f: ChatFilter) => void; phone?: boolean;
}) {
  const [view, setView] = useState<View>('all')
  if (!dests.some((d) => d.chat)) {
    return <ChatsSoon dests={dests} accounts={accounts} now={now} connecting={connecting} onConnect={onConnect} onOutputs={onOutputs} onFix={onFix} />
  }
  const on = dests.filter((d) => d.enabled)
  const reading = on.filter((d) => d.chat?.access === 'reads')
  const notReading = on.filter((d) => d.chat?.access !== 'reads' || d.chat.state === 'reconnecting')
  const live = dests.some((d) => d.state === 'live')
  const strip = <WatchStrip dests={dests} now={now} onOutputs={onOutputs} onFix={onFix} />

  if (!live && chat.items.length === 0) {
    return (
      <div data-testid="chats" data-chat="prelive" className="flex min-h-0 flex-1 flex-col">
        {strip}
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
          <p data-testid="chat-prelive" className="m-0 text-sm font-medium text-text">{t('chat.prelive')}</p>
          {on.length === 0 ? <p className="m-0 text-[13px] text-muted">{t('chats.noOutputs')}</p> : (
            <ul className="m-0 flex list-none flex-col p-0" aria-label={t('chats.access')}>
              {on.map((d) => <AccessLine key={d.id} d={d} connecting={connecting} onConnect={onConnect} />)}
            </ul>
          )}
          <p className="m-0 text-xs text-muted">{t('chat.preliveNote')}</p>
        </div>
      </div>
    )
  }

  const out = filter !== 'all' && reading.some((d) => d.outputId === filter) ? filter : 'all'
  const messages = chat.items.filter((i) => i.e.kind !== 'system')
  const count = (id: string) => messages.filter((i) => i.e.output_id === id).length
  const byOut = out === 'all' ? chat.items : chat.items.filter((i) => i.e.output_id === out)
  const isQ = (i: ChatItem) => i.e.kind === 'message' && i.e.text.includes('?')
  const isH = (i: ChatItem) => HIGHLIGHT_KINDS.includes(i.e.kind)
  const shown = view === 'qa' ? byOut.filter(isQ) : view === 'highlights' ? byOut.filter(isH) : byOut
  const VIEWS: [View, string][] = [['all', t('chat.view.all')], ['qa', `${t('chat.view.qa')} ${byOut.filter(isQ).length}`], ['highlights', `${t('chat.view.highlights')} ${byOut.filter(isH).length}`]]

  return (
    <div data-testid="chats" data-chat="feed" className="flex min-h-0 flex-1 flex-col">
      {strip}
      <div className="flex flex-none flex-col gap-2 border-b border-line p-2.5">
        <div className="flex gap-1 overflow-x-auto" role="radiogroup" aria-label={t('chat.filterLabel')} data-testid="chat-filters">
          <Chip on={out === 'all'} onClick={() => onFilter('all')} testId="chat-filter-all">
            {t('chat.all')} <span className="num font-mono text-[10px] text-muted">{messages.length}</span>
          </Chip>
          {reading.map((d) => {
            const u = chat.unread.byOutput[d.outputId] ?? 0
            return (
              <Chip key={d.id} on={out === d.outputId} onClick={() => onFilter(d.outputId)} title={d.name} testId={`chat-filter-${d.id}`}>
                <Mark platform={d.platform} size={16} />
                <span className="max-w-[88px] truncate">{d.name}</span>
                <span className="num font-mono text-[10px] text-muted">{count(d.outputId)}</span>
                {u > 0 && <span className="num font-mono text-[10px] text-accent">+{u}</span>}
              </Chip>
            )
          })}
        </div>
        <div className="flex items-center gap-1" role="radiogroup" aria-label={t('chat.viewLabel')}>
          {VIEWS.map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={view === k} data-testid={`chat-view-${k}`} onClick={() => setView(k)}
              className={`min-h-7 whitespace-nowrap rounded-sm px-1.5 text-xs max-md:min-h-[40px] ${view === k ? 'bg-accent/[.12] text-accent' : 'text-muted hover:text-text-soft'}`}>{l}</button>
          ))}
          {(chat.conn === 'reconnecting' || chat.conn === 'limited') && (
            <span data-testid="chat-conn" className="ml-auto truncate text-[11px] text-warn">{chat.conn === 'limited' ? t('chat.limited') : t('chat.reconnecting')}</span>
          )}
        </div>
      </div>
      {shown.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col p-4 max-md:min-h-[160px]">
          <p data-testid="chat-empty" className="m-0 text-center text-[13px] text-muted">{chat.items.length === 0 ? t('chat.empty') : t('chat.emptyView')}</p>
        </div>
      ) : (
        <Feed items={shown} dests={dests} phone={phone} resetKey={`${out}|${view}`} />
      )}
      {notReading.length > 0 && (
        <ul data-testid="chat-not-reading" aria-label={t('chat.notReading')} className="m-0 flex max-h-[30%] flex-none list-none flex-col overflow-y-auto border-t border-line px-3 py-1">
          {notReading.map((d) => <AccessLine key={d.id} d={d} connecting={connecting} onConnect={onConnect} />)}
        </ul>
      )}
      <div className="flex flex-none items-center gap-2 border-t border-line px-3 py-2 text-[11px] text-muted">
        <span className="flex-1">{t('chat.reply')}</span>
        <Soon />
      </div>
    </div>
  )
}
